import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiArrowUp, FiArrowDown, FiArrowLeft, FiMoreVertical, FiSlash, FiShield, FiAlertTriangle, FiInfo, FiTrash2, FiRefreshCw, FiFlag, FiMessageSquare } from 'react-icons/fi';
import ReportModal from '../../components/dashboard/ReportModal';
import { useSocket } from '../../context/SocketContext';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import Picker, { Emoji, EmojiStyle } from 'emoji-picker-react';
import { useTheme } from '../../context/ThemeContext';
import { API_BASE_URL, buildMediaUrl } from '../../config/api';
import { fetchJson, formatFetchError } from '../../utils/fetchJson';
import { getProfilePhotoUrl } from '../../utils/profileUtils';



const getMessageImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (typeof imagePath === 'string' && (imagePath.startsWith('http://') || imagePath.startsWith('https://'))) {
    return imagePath;
  }
  return buildMediaUrl(imagePath);
};

const isImageMessagePayload = (msg) => {
  if (!msg) return false;
  return (
    msg.messageType === 'image' ||
    msg.type === 'image' ||
    Boolean(msg.image) ||
    Boolean(msg.imageUrl)
  );
};

const getMessagePreviewText = (msg) => {
  if (!msg) return 'No messages yet';
  if (isImageMessagePayload(msg)) {
    return '📷 Photo';
  }
  const content = msg.text || msg.message;
  if (typeof content === 'string') return content;
  if (typeof content === 'object' && content !== null) {
    // Try to dig deeper if it's nested
    return content.text || content.message || ' Sent a message';
  }
  return 'No messages yet';
};

const formatMessageTime = (date) => {
  if (!date) return '';
  const messageDate = new Date(date);
  if (isNaN(messageDate.getTime())) return ''; // valid date check
  const now = new Date();
  const diffInHours = Math.floor((now - messageDate) / (1000 * 60 * 60));

  if (diffInHours < 1) {
    const diffInMinutes = Math.floor((now - messageDate) / (1000 * 60));
    return diffInMinutes < 1 ? 'Just now' : `${diffInMinutes} min ago`;
  } else if (diffInHours < 24) {
    return messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } else if (diffInHours < 48) {
    return 'Yesterday';
  } else {
    return messageDate.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }
};

const MESSAGE_IMAGE_MAX_MB = 15;
const MESSAGE_IMAGE_MAX_BYTES = MESSAGE_IMAGE_MAX_MB * 1024 * 1024;

const Messages = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { success, warning, error: showError } = useToast();
  const { theme } = useTheme();
  const {
    socket,
    onlineUsers,
    joinConversation,
    leaveConversation,
    startTyping,
    stopTyping
  } = useSocket();

  const resolvedUserId = user?._id || user?.id;

  const [conversations, setConversations] = useState(() => {
    try {
      const cached = localStorage.getItem('cachedConversations');
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });

  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messageInput, setMessageInput] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [typingUser, setTypingUser] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [, setLoading] = useState(() => !localStorage.getItem('cachedConversations'));
  const [, setError] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [showChatList, setShowChatList] = useState(true);
  const [hoveredConversationId, setHoveredConversationId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBlocking, setIsBlocking] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [isReporting, setIsReporting] = useState(false);
  const [messageDropdownId, setMessageDropdownId] = useState(null);
  const [showMessageReportModal, setShowMessageReportModal] = useState(false);
  const [messageToReport, setMessageToReport] = useState(null);
  const [showReportInstructionBanner, setShowReportInstructionBanner] = useState(false);
  const [menuOpenItemId, setMenuOpenItemId] = useState(null);

  // Message request state
  const [pendingRequests, setPendingRequests] = useState([]);       // incoming requests awaiting my decision
  const [isRespondingRequest, setIsRespondingRequest] = useState(false);
  const [activeTab, setActiveTab] = useState('chats'); // 'chats' | 'requests'


  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const dropdownRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const emojiButtonRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);

  const activeConversationRoomId = useMemo(() => {
    const otherUserId = selectedConversation?.id;
    if (!resolvedUserId || !otherUserId) return null;
    const [a, b] = [String(resolvedUserId), String(otherUserId)].sort();
    return `conv_${a}_${b}`;
  }, [resolvedUserId, selectedConversation?.id]);

  const markMessagesAsRead = useCallback(async (partnerId) => {
    try {
      const token = localStorage.getItem('token');

      if (!token) {
        console.log('No auth token found');
        return;
      }

      console.log(`📖 Marking messages as read for ${partnerId ? partnerId : 'all'}`);
      const { response } = await fetchJson(`${API_BASE_URL}/api/messages/mark-all-read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ userId: partnerId }),
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.log('Token expired in markMessagesAsRead');
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      console.log('✅ Messages marked as read');
      // Notify other parts of the app (e.g., sidebar) to clear unread badge
      window.dispatchEvent(new Event('messages_read'));
    } catch (err) {
      console.error('Error marking messages as read:', err);
    }
  }, []);

  // Render message text with Facebook-style emoji glyphs
  const emojiRegex = /(\p{Extended_Pictographic}(?:\uFE0F|\u200D\p{Extended_Pictographic})*)/gu;
  const isEmojiSegment = (segment) => {
    if (!segment) return false;
    emojiRegex.lastIndex = 0;
    return emojiRegex.test(segment);
  };
  const toUnified = (segment) => segment
    .split('\u200d')
    .map(part => [...part].map(ch => ch.codePointAt(0).toString(16)).join('-'))
    .join('-200d-');
  const renderEmojiText = (text) => {
    if (!text || typeof text !== 'string') return null;
    const parts = text.split(emojiRegex).filter(Boolean);
    return parts.map((part, idx) =>
      isEmojiSegment(part) ? (
        <Emoji
          key={`emoji-${idx}`}
          unified={toUnified(part)}
          size={22}
          emojiStyle={EmojiStyle.FACEBOOK}
        />
      ) : (
        <span key={`text-${idx}`}>{part}</span>
      )
    );
  };

  // Render emoji text small (for chat list previews, etc.)
  const renderEmojiTextSmall = (text) => {
    if (!text || typeof text !== 'string') return null;
    const parts = text.split(emojiRegex).filter(Boolean);
    return parts.map((part, idx) =>
      isEmojiSegment(part) ? (
        <Emoji
          key={`emoji-small-${idx}`}
          unified={toUnified(part)}
          size={18}
          emojiStyle={EmojiStyle.FACEBOOK}
        />
      ) : (
        <span key={`text-small-${idx}`}>{part}</span>
      )
    );
  };

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close message dropdown
      if (messageDropdownId) {
        setMessageDropdownId(null);
      }

      // Close side-menu
      if (menuOpenItemId) {
        setMenuOpenItemId(null);
      }

      // Close emoji picker
      if (
        showEmojiPicker &&
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target) &&
        emojiButtonRef.current &&
        !emojiButtonRef.current.contains(event.target)
      ) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [messageDropdownId, showEmojiPicker, menuOpenItemId]);

  // Handle conversation or request selection
  const handleSelectConversation = useCallback((item) => {
    const itemIdStr = item.id?.toString();
    
    // If it's a regular conversation
    if (item.type !== 'request') {
      setConversations(prev => prev.map(conv =>
        conv.id?.toString() === itemIdStr ? { ...conv, unread: 0 } : conv
      ));
      setSelectedConversation(item);
      setIsBlocked(!!item.blocked);
      markMessagesAsRead(itemIdStr);
    } else {
      // It's a message request
      setSelectedConversation(item);
      setIsBlocked(false);
    }

    if (isMobile) {
      setShowChatList(false);
    }
  }, [isMobile, markMessagesAsRead]);

  // Handle back to chat list for mobile
  const handleBackToChatList = () => {
    setSelectedConversation(null);
    if (isMobile) {
      setShowChatList(true);
    }
  };

  // Add initial loading state to prevent empty flash
  const [initialLoading, setInitialLoading] = useState(true);

  // Cache conversations in localStorage for instant loading
  const getCachedConversations = useCallback(() => {
    try {
      const cached = localStorage.getItem('cachedConversations');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  }, []);

  const setCachedConversations = useCallback((conversations) => {
    try {
      // Only cache conversations that don't include current user's own profile
      const filteredConversations = conversations.filter(conv => {
        const convUserId = conv.id?.toString();
        const currentUserId = user?.id?.toString();
        return convUserId !== currentUserId;
      }).map(conv => ({
        ...conv,
        user: {
          ...conv.user,
          lastActive: onlineUsers.has(conv.id.toString()) ? 'Active now' : 'Offline'
        }
      }));
      localStorage.setItem('cachedConversations', JSON.stringify(filteredConversations));
    } catch {
      // Ignore cache errors
    }
  }, [onlineUsers, user?.id]);

  const updateConversationsState = useCallback((updater) => {
    setConversations(prev => {
      const updated = updater(prev);
      setCachedConversations(updated);
      return updated;
    });
  }, [setCachedConversations]);

  const fetchConversations = useCallback(async () => {
    // Show cached conversations instantly if available
    const cachedConversations = getCachedConversations();
    if (cachedConversations) {
      // Filter out current user's own profile from cached conversations as well
      const filteredCachedConversations = cachedConversations.filter(conv => {
        const convUserId = conv.id?.toString();
        const currentUserId = user?.id?.toString();
        return convUserId !== currentUserId;
      }).map(conv => ({
        ...conv,
        user: {
          ...conv.user,
          lastActive: onlineUsers.has(conv.id.toString()) ? 'Active now' : 'Offline'
        },
        blocked: !!(conv.blocked || conv.isBlocked)
      }));
      setConversations(filteredCachedConversations);
      setInitialLoading(false); // Hide skeleton immediately
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');

      if (!token) {
        console.log('No auth token found');
        setError('Please login to view messages');
        setLoading(false);
        setInitialLoading(false);
        return;
      }

      const { response, data } = await fetchJson(`${API_BASE_URL}/api/messages/conversations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.log('Token expired in fetchConversations');
          setError('Session expired. Please login again.');
          setLoading(false);
          setInitialLoading(false);
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (data?.success) {
        // Transform backend data to frontend format and filter out current user's own profile
        const rawConversations = Array.isArray(data?.data?.conversations) ? data.data.conversations : [];
        const transformedConversations = rawConversations
          .filter(conv => {
            // Filter out conversations with current user's own profile
            // Check if conversation user ID matches current user ID
            const convUserId = conv.id?.toString();
            const currentUserId = user?.id?.toString();
            return convUserId !== currentUserId;
          })
          .map(conv => ({
            id: conv.id,
            user: {
              name: `${conv.firstName} ${conv.lastName}`,
              photo: getProfilePhotoUrl(conv),
              lastActive: 'Offline'
            },
            blocked: !!(conv.blocked || conv.isBlocked),
            lastMessage: getMessagePreviewText(conv.lastMessage),
            unread: conv.unreadCount || 0,
            timestamp: formatMessageTime(conv.lastMessage?.sentAt),
            messages: (Array.isArray(conv.messages) ? conv.messages : []).map(msg => {
              // Check if message is from current user - handle populated ObjectId correctly
              const senderId = msg.from?._id || msg.from; // Handle both populated and non-populated
              const isFromMe = senderId?.toString() === user?.id;
              console.log('🔍 Message data:', {
                msgFrom: msg.from,
                msgFromId: msg.from?._id || msg.from,
                msgFromToString: senderId?.toString(),
                userId: user?.id,
                isFromMe,
                message: msg.message
              });
              return {
                id: msg._id || Date.now().toString(),
                text: (typeof msg.message === 'string' ? msg.message :
                  typeof msg.message?.text === 'string' ? msg.message.text :
                    typeof msg.message?.message === 'string' ? msg.message.message : ''),
                sentAt: msg.sentAt || msg.createdAt || msg.timestamp || null,
                timestamp: formatMessageTime(msg.sentAt),
                sender: isFromMe ? 'me' : 'them',
                senderId: senderId, // Store the actual sender ID
                from: msg.from, // Keep original from field for debugging
                senderInfo: {
                  id: msg.from?._id || msg.from,
                  name: msg.from?.firstName && msg.from?.lastName ?
                    `${msg.from.firstName} ${msg.from.lastName}` :
                    msg.from?.name || 'Unknown',
                  photo: getProfilePhotoUrl(msg.from?.profilePhoto)
                },
                type: msg.messageType === 'image' || msg.image ? 'image' : 'text',
                imageUrl: getMessageImageUrl(msg.image)
              };
            })
          }));

        setConversations(transformedConversations);
        setCachedConversations(transformedConversations); // Update cache
        console.log('📨 Conversations fetched:', transformedConversations);
      } else {
        setError(data?.message || 'Failed to fetch conversations');
      }
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError(formatFetchError(err, 'Failed to fetch conversations. Please try again.'));
    } finally {
      setLoading(false);
      setInitialLoading(false); // Ensure initial loading is false
    }
  }, [user?.id, onlineUsers, getCachedConversations, setCachedConversations]);

  // ── Fetch pending incoming message requests ──────────────────
  const fetchPendingRequests = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const { response, data } = await fetchJson(`${API_BASE_URL}/api/messages/requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok && data.success) {
        setPendingRequests(data.data.requests || []);
      }
    } catch (err) {
      console.error('Error fetching pending requests:', err);
    }
  }, []);

  // Fetch conversations (and pending requests) from backend when user data is ready
  useEffect(() => {
    if (user && user.id) {
      fetchConversations();
      fetchPendingRequests();
    }
  }, [user, fetchConversations, fetchPendingRequests]);

  // Keep selected conversation block state in sync when conversations update (e.g., after refresh)
  useEffect(() => {
    if (!selectedConversation) return;
    const latest = conversations.find(conv => conv.id === selectedConversation.id);
    if (latest) {
      setSelectedConversation(prev => ({ ...prev, ...latest }));
      setIsBlocked(!!latest.blocked);
    }
  }, [conversations, selectedConversation, selectedConversation?.id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const [userScrolled, setUserScrolled] = useState(false);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const lastScrollPositionRef = useRef(0);
  const isAtBottomRef = useRef(true);
  const programmaticScrollRef = useRef(false);
  const userHasScrolledUpRef = useRef(false);
  const userScrolledRef = useRef(false);
  const autoScrollEnabledRef = useRef(true);

  const BOTTOM_THRESHOLD_PX = 2;

  useEffect(() => {
    userScrolledRef.current = userScrolled;
  }, [userScrolled]);

  useEffect(() => {
    autoScrollEnabledRef.current = autoScrollEnabled;
  }, [autoScrollEnabled]);

  const scrollToBottom = useCallback((force = false) => {
    // Only scroll if forced or if auto-scroll is enabled and user hasn't manually scrolled
    if (!force && (!autoScrollEnabledRef.current || userScrolledRef.current || userHasScrolledUpRef.current)) {
      return;
    }

    if (messagesContainerRef.current) {
      // Use requestAnimationFrame for smoother scrolling
      programmaticScrollRef.current = true;
      userHasScrolledUpRef.current = false;
      requestAnimationFrame(() => {
        const container = messagesContainerRef.current;
        if (container) {
          // Scroll to the very bottom to ensure input field is visible
          container.scrollTop = container.scrollHeight;
          isAtBottomRef.current = true;
          lastScrollPositionRef.current = container.scrollTop;
        }
      });

      // Give the browser a moment to fire the scroll event
      setTimeout(() => {
        programmaticScrollRef.current = false;
        if (messagesContainerRef.current) {
          lastScrollPositionRef.current = messagesContainerRef.current.scrollTop;
        }
      }, 200);
    }
  }, []);

  useEffect(() => {
    // Only auto-scroll if enabled, user hasn't manually scrolled, and they're at the bottom
    if (autoScrollEnabled && !userScrolled && messagesContainerRef.current) {
      if (isAtBottomRef.current && !userHasScrolledUpRef.current) {
        scrollToBottom(true);
      }
    }
  }, [selectedConversation?.messages?.length, selectedConversation?.id, autoScrollEnabled, userScrolled, scrollToBottom]); // Only depend on messages array length

  // Also scroll when conversation is selected, but reset userScrolled state
  useEffect(() => {
    if (selectedConversation?.id) {
      setUserScrolled(false); // Reset scroll state when switching conversations
      setAutoScrollEnabled(true); // Re-enable auto-scroll for new conversation
      userHasScrolledUpRef.current = false;
      lastScrollPositionRef.current = 0; // Reset scroll position
      // Small delay to ensure messages are rendered
      setTimeout(() => scrollToBottom(true), 100); // Force scroll on conversation change
    }
  }, [selectedConversation?.id, scrollToBottom]); // Only depend on conversation ID, not the whole object

  // Handle scroll detection for scroll-to-top button
  useEffect(() => {
    let scrollTimeout;

    const handleScroll = () => {
      if (!messagesContainerRef.current) return;

      const container = messagesContainerRef.current;
      const { scrollTop, scrollHeight, clientHeight } = container;
      const currentScrollPosition = scrollTop;

      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
      const isAtBottomNow = distanceFromBottom <= BOTTOM_THRESHOLD_PX;
      isAtBottomRef.current = isAtBottomNow;

      // Detect if this is a manual scroll (not programmatic)
      const scrollDirection = currentScrollPosition > lastScrollPositionRef.current ? 'down' : 'up';
      const scrollDistance = Math.abs(currentScrollPosition - lastScrollPositionRef.current);

      // Any manual upward scroll should lock auto-scroll off immediately (WhatsApp-like)
      if (!programmaticScrollRef.current && scrollDirection === 'up' && scrollDistance > 0) {
        // Disable auto-scroll immediately so new messages (or state updates) won't pull them down
        userHasScrolledUpRef.current = true;
        isAtBottomRef.current = false;
        setUserScrolled(true);
        setAutoScrollEnabled(false);
      } else if (!programmaticScrollRef.current && isAtBottomNow) {
        userHasScrolledUpRef.current = false;
      }

      // Debounce scroll events to prevent performance issues
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        if (!messagesContainerRef.current) return;

        const { scrollTop: st, scrollHeight: sh, clientHeight: ch } = messagesContainerRef.current;
        const distance = sh - (st + ch);
        const atBottom = distance <= BOTTOM_THRESHOLD_PX;
        isAtBottomRef.current = atBottom;
        if (!programmaticScrollRef.current) {
          userHasScrolledUpRef.current = !atBottom;
        }

        setShowScrollTop(st > 300); // Show button after scrolling down 300px
        setShowScrollBottom(!atBottom); // Show button if not at bottom

        // Lock/unlock auto-scroll purely based on whether the user is at the bottom
        if (!programmaticScrollRef.current) {
          if (!atBottom) {
            setUserScrolled(true);
            setAutoScrollEnabled(false);
          } else {
            setUserScrolled(false);
          }
        }
      }, 150); // Slightly longer debounce delay

      // Update last scroll position
      lastScrollPositionRef.current = currentScrollPosition;
    };

    const container = messagesContainerRef.current;
    if (container) {
      // Use passive event listener for better performance
      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        clearTimeout(scrollTimeout);
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, [selectedConversation?.id]); // Only depend on conversation ID, not the whole object

  const scrollToTop = () => {
    if (messagesContainerRef.current) {
      // Set userScrolled to true since this is manual scroll action
      setUserScrolled(true);
      setAutoScrollEnabled(false); // Disable auto-scroll when user manually scrolls to top
      lastScrollPositionRef.current = messagesContainerRef.current.scrollTop;
      // Use requestAnimationFrame for smoother scrolling
      requestAnimationFrame(() => {
        const container = messagesContainerRef.current;
        if (container) {
          container.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          lastScrollPositionRef.current = 0;
        }
      });
    }
  };

  // Update online status in real-time
  useEffect(() => {
    // Update conversations list with current online status
    setConversations(prev => prev.map(conv => ({
      ...conv,
      user: {
        ...conv.user,
        lastActive: onlineUsers.has(conv.id.toString()) ? 'Active now' : 'Offline'
      }
    })));

    // Update selected conversation online status
    if (selectedConversation) {
      setSelectedConversation(prev => ({
        ...prev,
        user: {
          ...prev.user,
          lastActive: onlineUsers.has(prev.id.toString()) ? 'Active now' : 'Offline'
        }
      }));
    }
  }, [onlineUsers, selectedConversation, selectedConversation?.id]);

  // Socket.io message handling using custom events to prevent duplicates
  useEffect(() => {
    const handleSocketMessage = (event) => {
      const message = event.detail;
      const fromUserId = message?.senderId || message?.from;
      if (!fromUserId) return;

      const normalizedFromId = fromUserId.toString();
      const incomingImagePath = message?.imageUrl || message?.image || null;
      const detectionPayload = {
        ...message,
        imageUrl: incomingImagePath
      };
      const isImage = isImageMessagePayload(detectionPayload);
      const sentAtIso = message?.timestamp || new Date().toISOString();
      const previewPayload = {
        messageType: message?.messageType,
        text: message?.text || message?.message || '',
        image: incomingImagePath,
        imageUrl: incomingImagePath
      };

      const newMessage = {
        id: message?.id || message?._id || Date.now().toString(),
        text: (typeof message?.text === 'string' ? message.text :
          typeof message?.message === 'string' ? message.message :
            typeof message?.message?.text === 'string' ? message.message.text :
              typeof message?.message?.message === 'string' ? message.message.message : ''),
        sentAt: sentAtIso,
        timestamp: formatMessageTime(sentAtIso),
        sender: 'them',
        senderId: normalizedFromId,
        senderInfo: {
          id: normalizedFromId,
          name: message?.senderName || message?.sender?.name || 'User',
          photo: getProfilePhotoUrl({
            profilePhoto: message?.senderPhoto || message?.sender?.photo || message?.sender?.profilePhoto,
            gender: message?.senderGender || message?.sender?.gender
          })
        },
        type: isImage ? 'image' : 'text',
        imageUrl: isImage && incomingImagePath ? getMessageImageUrl(incomingImagePath) : null
      };

      if (selectedConversation?.id?.toString() === normalizedFromId) {
        // Do not force-scroll here. The scroll listener controls when auto-scroll is enabled.
        setSelectedConversation(prev => {
          if (!prev || prev.id?.toString() !== normalizedFromId) return prev;
          return {
            ...prev,
            messages: [...prev.messages, newMessage],
            lastMessage: getMessagePreviewText(newMessage),
            timestamp: 'Just now'
          };
        });

        markMessagesAsRead(normalizedFromId);

        updateConversationsState(prev => prev.map(conv =>
          conv.id?.toString() === normalizedFromId
            ? {
              ...conv,
              messages: [...conv.messages, newMessage],
              lastMessage: getMessagePreviewText(newMessage),
              timestamp: 'Just now'
            }
            : conv
        ));
      } else {
        updateConversationsState(prev => prev.map(conv => {
          if (conv.id?.toString() === normalizedFromId) {
            const nextUnread = (conv.unread || 0) + 1;
            return {
              ...conv,
              messages: [...(conv.messages || []), newMessage],
              unread: nextUnread,
              lastMessage: getMessagePreviewText(previewPayload),
              timestamp: 'Just now'
            };
          }
          return conv;
        }));
      }
    };

    const handleUserTyping = (event) => {
      const { conversationId, userName, isTyping } = event.detail || {};
      if (!activeConversationRoomId || !conversationId) return;
      if (String(conversationId) !== String(activeConversationRoomId)) return;
      setTypingUser(isTyping ? userName : '');
    };

    const handleConversationDeleted = (event) => {
      const { partnerId } = event.detail;
      const normalizedPartnerId = partnerId.toString();

      setConversations(prev => prev.filter(conv => conv.id?.toString() !== normalizedPartnerId));
      setCachedConversations(prev => prev.filter(conv => conv.id?.toString() !== normalizedPartnerId));

      if (selectedConversation?.id?.toString() === normalizedPartnerId) {
        setSelectedConversation(null);
        success('This conversation was deleted from another device.');
      }
    };

    // Real-time: our message request was accepted/declined by the recipient
    const handleRequestResponded = (event) => {
      const { action, recipientName } = event.detail || {};
      if (action === 'accepted') {
        success(`🎉 ${recipientName || 'Someone'} accepted your message request! You can now start chatting.`);
        fetchConversations();
      }
    };

    // Real-time: we received a NEW incoming message request — add to sidebar immediately
    const handleRequestReceived = (event) => {
      const { from } = event.detail || {};
      if (from?.id) {
        // Add to pending requests list if not already present
        setPendingRequests(prev => {
          const fromIdString = from.id.toString();
          const alreadyExists = prev.some(r => {
            const rid = (r.from?._id || r.from || '').toString();
            return rid === fromIdString;
          });
          if (alreadyExists) return prev;
          
          return [
            {
              from: {
                _id: from.id,
                firstName: from.firstName,
                lastName: from.lastName || '',
                profilePhoto: from.profilePhoto
              },
              status: 'pending',
              sentAt: new Date().toISOString()
            },
            ...prev
          ];
        });
        success(`💌 ${from.firstName} ${from.lastName || ''} sent you a message request!`);
      }
    };

    // Listen for custom events from SocketContext
    window.addEventListener('socket_message', handleSocketMessage);
    window.addEventListener('socket_typing', handleUserTyping);
    window.addEventListener('socket_conversation_deleted', handleConversationDeleted);
    window.addEventListener('socket_message_request_responded', handleRequestResponded);
    window.addEventListener('socket_message_request_received', handleRequestReceived);

    return () => {
      window.removeEventListener('socket_message', handleSocketMessage);
      window.removeEventListener('socket_typing', handleUserTyping);
      window.removeEventListener('socket_conversation_deleted', handleConversationDeleted);
      window.removeEventListener('socket_message_request_responded', handleRequestResponded);
      window.removeEventListener('socket_message_request_received', handleRequestReceived);
    };
  }, [selectedConversation, selectedConversation?.id, activeConversationRoomId, updateConversationsState, setCachedConversations, setConversations, success, fetchConversations, markMessagesAsRead]);

  // Join/leave conversation rooms
  useEffect(() => {
    if (activeConversationRoomId && socket) {
      joinConversation(activeConversationRoomId);
      return () => {
        leaveConversation(activeConversationRoomId);
      };
    }
  }, [activeConversationRoomId, socket, joinConversation, leaveConversation]);

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedConversation) return;

    const messageText = messageInput.trim();
    const conversationId = selectedConversation.id;
    const tempId = `temp-text-${Date.now()}`;
    const sentAtIso = new Date().toISOString();

    if (!userHasScrolledUpRef.current) {
      setUserScrolled(false);
      setAutoScrollEnabled(true);
    }

    const tempMessage = {
      id: tempId,
      text: messageText,
      sentAt: sentAtIso,
      timestamp: 'Just now',
      sender: 'me',
      senderId: resolvedUserId,
      type: 'text',
      temp: true,
      status: 'sent'
    };

    setSelectedConversation(prev => {
      if (!prev || prev.id !== conversationId) return prev;
      return {
        ...prev,
        messages: [...prev.messages, tempMessage],
        lastMessage: getMessagePreviewText(tempMessage),
        timestamp: 'Just now'
      };
    });

    updateConversationsState(prev => prev.map(conv =>
      conv.id === conversationId
        ? {
          ...conv,
          messages: [...conv.messages, tempMessage],
          lastMessage: getMessagePreviewText(tempMessage),
          timestamp: 'Just now'
        }
        : conv
    ));

    setMessageInput('');
    setShowEmojiPicker(false);

    try {
      const token = localStorage.getItem('token');
      const { response, data } = await fetchJson(`${API_BASE_URL}/api/messages/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipientId: conversationId,
          message: messageText
        })
      });

      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to send message');
      }

      const messagePayload = data?.data?.message || {};
      const recipientOnline = onlineUsers.has(conversationId.toString());
      const sentAtServer = messagePayload?.sentAt || sentAtIso;
      const realMessage = {
        id: messagePayload?.id || messagePayload?._id || tempId,
        text: messagePayload?.message || messageText,
        sentAt: sentAtServer,
        timestamp: formatMessageTime(sentAtServer),
        sender: 'me',
        senderId: resolvedUserId,
        type: 'text',
        temp: false,
        status: messagePayload?.status || (recipientOnline ? 'delivered' : 'sent'),
        isRead: messagePayload?.isRead || false
      };

      setSelectedConversation(prev => {
        if (!prev || prev.id !== conversationId) return prev;
        return {
          ...prev,
          messages: prev.messages.map(msg =>
            msg.id === tempId ? realMessage : msg
          ),
          lastMessage: getMessagePreviewText(realMessage),
          timestamp: 'Just now'
        };
      });

      updateConversationsState(prev => prev.map(conv =>
        conv.id === conversationId
          ? {
            ...conv,
            messages: conv.messages.map(msg =>
              msg.id === tempId ? realMessage : msg
            ),
            lastMessage: getMessagePreviewText(realMessage),
            timestamp: 'Just now'
          }
          : conv
      ));

      if (socket && activeConversationRoomId && resolvedUserId) {
        socket.emit('send_message', {
          conversationId: activeConversationRoomId,
          message: {
            text: realMessage.text,
            messageType: 'text',
            senderName: user?.firstName || 'Someone',
            senderPhoto: user?.profilePhoto
          },
          senderId: resolvedUserId,
          receiverId: conversationId
        });
      }

      console.log('✅ Message sent successfully');
    } catch (err) {
      console.error('Error sending message:', err);

      setSelectedConversation(prev => {
        if (!prev || prev.id !== conversationId) return prev;
        return {
          ...prev,
          messages: prev.messages.filter(msg => msg.id !== tempId)
        };
      });

      updateConversationsState(prev => prev.map(conv =>
        conv.id === conversationId
          ? { ...conv, messages: conv.messages.filter(msg => msg.id !== tempId) }
          : conv
      ));

      setError(formatFetchError(err, 'Failed to send message'));
      setMessageInput(messageText);
    }
  };

  const handleMessageChange = (e) => {
    setMessageInput(e.target.value);

    if (!isTyping && activeConversationRoomId && selectedConversation) {
      setIsTyping(true);
      startTyping(activeConversationRoomId, user?.firstName || 'You');
    }

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set new timeout to stop typing indicator
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      if (activeConversationRoomId) {
        stopTyping(activeConversationRoomId);
      }
    }, 1000);
  };

  const handleEmojiSelect = (emoji) => {
    setMessageInput(prev => prev + emoji.emoji);
    setShowEmojiPicker(false);
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!selectedConversation) {
      showError('Please select a conversation first.');
      event.target.value = '';
      return;
    }

    if (file.size > MESSAGE_IMAGE_MAX_BYTES) {
      showError(`Image must be smaller than ${MESSAGE_IMAGE_MAX_MB}MB.`);
      event.target.value = '';
      return;
    }

    const conversationId = selectedConversation.id;
    const tempId = `temp-image-${Date.now()}`;
    const previewUrl = URL.createObjectURL(file);

    if (!userHasScrolledUpRef.current) {
      setUserScrolled(false);
      setAutoScrollEnabled(true);
    }

    const tempMessage = {
      id: tempId,
      text: '',
      sentAt: new Date().toISOString(),
      timestamp: 'Just now',
      sender: 'me',
      senderId: resolvedUserId,
      type: 'image',
      imageUrl: previewUrl,
      temp: true,
      status: 'sent'
    };

    setSelectedConversation(prev => {
      if (!prev || prev.id !== conversationId) return prev;
      return {
        ...prev,
        messages: [...prev.messages, tempMessage],
        lastMessage: getMessagePreviewText(tempMessage),
        timestamp: 'Just now'
      };
    });

    updateConversationsState(prev => prev.map(conv =>
      conv.id === conversationId
        ? {
          ...conv,
          messages: [...conv.messages, tempMessage],
          lastMessage: getMessagePreviewText(tempMessage),
          timestamp: 'Just now'
        }
        : conv
    ));

    setUploadingImage(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Please login again to send images.');
      }

      const formData = new FormData();
      formData.append('image', file);
      formData.append('recipientId', conversationId);

      const response = await fetch(`${API_BASE_URL}/api/messages/send-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to send image.');
      }

      const messagePayload = data?.data?.message || {};
      const recipientOnline = onlineUsers.has(conversationId.toString());
      const sentAtServer = messagePayload?.sentAt || new Date().toISOString();
      const backendImagePath = messagePayload?.image || messagePayload?.imageUrl || '';
      const realImageUrl = getMessageImageUrl(backendImagePath) || previewUrl;
      const realMessage = {
        id: messagePayload?.id || messagePayload?._id || tempId,
        text: messagePayload?.message || '',
        sentAt: sentAtServer,
        timestamp: formatMessageTime(sentAtServer),
        sender: 'me',
        senderId: resolvedUserId,
        type: 'image',
        imageUrl: realImageUrl,
        temp: false,
        status: messagePayload?.status || (recipientOnline ? 'delivered' : 'sent'),
        isRead: messagePayload?.isRead || false
      };

      setSelectedConversation(prev => {
        if (!prev || prev.id !== conversationId) return prev;
        return {
          ...prev,
          messages: prev.messages.map(msg =>
            msg.id === tempId ? realMessage : msg
          ),
          lastMessage: getMessagePreviewText(realMessage),
          timestamp: 'Just now'
        };
      });

      updateConversationsState(prev => prev.map(conv =>
        conv.id === conversationId
          ? {
            ...conv,
            messages: conv.messages.map(msg =>
              msg.id === tempId ? realMessage : msg
            ),
            lastMessage: getMessagePreviewText(realMessage),
            timestamp: 'Just now'
          }
          : conv
      ));

      if (socket && activeConversationRoomId && resolvedUserId) {
        socket.emit('send_message', {
          conversationId: activeConversationRoomId,
          message: {
            text: realMessage.text,
            messageType: 'image',
            imageUrl: backendImagePath || realImageUrl,
            senderName: user?.firstName || 'Someone',
            senderPhoto: user?.profilePhoto
          },
          senderId: resolvedUserId,
          receiverId: conversationId
        });
      }
    } catch (error) {
      console.error('Error sending image:', error);
      showError(error.message || 'Failed to send image.');

      setSelectedConversation(prev => {
        if (!prev || prev.id !== conversationId) return prev;
        return {
          ...prev,
          messages: prev.messages.filter(msg => msg.id !== tempId),
          lastMessage: prev.lastMessage === getMessagePreviewText(tempMessage) ? prev.messages[prev.messages.length - 1]?.text || prev.lastMessage : prev.lastMessage
        };
      });

      updateConversationsState(prev => prev.map(conv =>
        conv.id === conversationId
          ? { ...conv, messages: conv.messages.filter(msg => msg.id !== tempId) }
          : conv
      ));
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      URL.revokeObjectURL(previewUrl);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleDeleteConversation = async (convId = null) => {
    const targetId = convId || (selectedConversation?.id?.toString());
    if (!targetId) return;

    setIsDeleting(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Please login again to delete this conversation.');
        return;
      }

      const { response, data } = await fetchJson(`${API_BASE_URL}/api/messages/conversations/${targetId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok || !data.success) {
        setError(data.message || 'Failed to delete conversation');
        setIsDeleting(false);
        return;
      }

      // Update conversations list (active chats)
      setConversations(prev => prev.filter(conv => conv.id?.toString() !== targetId?.toString()));
      
      // Update pending requests list (to remove request UI items)
      setPendingRequests(prev => prev.filter(req => {
        const partner = req.from?._id?.toString() === resolvedUserId?.toString() ? req.to : req.from;
        const partnerId = (partner?._id || partner?.id || partner)?.toString();
        return partnerId !== targetId?.toString();
      }));

      // Clear selected conversation if it's the one we deleted
      if (selectedConversation?.id?.toString() === targetId?.toString()) {
        setSelectedConversation(null);
        setShowChatList(true); // show list on mobile if current chat was deleted
      }

      setShowDropdown(false);
      setMenuOpenItemId(null);
      
      // Update cache
      const updatedCache = conversations.filter(conv => conv.id?.toString() !== targetId?.toString());
      localStorage.setItem('cachedConversations', JSON.stringify(updatedCache));
      
      success('Conversation deleted successfully');
    } catch (err) {
      console.error('Error deleting conversation:', err);
      setError(formatFetchError(err, 'Failed to delete conversation. Please try again.'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBlockUser = async () => {
    if (!selectedConversation) return;

    setIsBlocking(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Please login again to block this user.');
        setIsBlocking(false);
        return;
      }

      const { response, data } = await fetchJson(`${API_BASE_URL}/api/user-profiles/${selectedConversation.id}/block`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok || !data.success) {
        setError(data.message || 'Failed to block user');
        setIsBlocking(false);
        return;
      }

      warning(`You have blocked ${selectedConversation.user.name}. They will no longer be able to message you.`);
      setIsBlocked(true);
      // Persist block flag locally for current and cached conversations
      setSelectedConversation(prev => prev ? { ...prev, blocked: true } : prev);
      setConversations(prev => prev.map(conv =>
        conv.id === selectedConversation.id ? { ...conv, blocked: true } : conv
      ));
      setCachedConversations(prev => prev ? prev.map(conv =>
        conv.id === selectedConversation.id ? { ...conv, blocked: true } : conv
      ) : []);
      setShowDropdown(false);
    } catch (err) {
      console.error('Error blocking user:', err);
      setError(formatFetchError(err, 'Failed to block user. Please try again.'));
    } finally {
      setIsBlocking(false);
    }
  };

  const handleUnblockUser = async () => {
    if (!selectedConversation) return;

    setIsBlocking(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Please login again to unblock this user.');
        setIsBlocking(false);
        return;
      }

      const { response, data } = await fetchJson(`${API_BASE_URL}/api/messages/unblock`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ userId: selectedConversation.id })
      });

      if (!response.ok || !data.success) {
        setError(data.message || 'Failed to unblock user');
        setIsBlocking(false);
        return;
      }

      success(`You have unblocked ${selectedConversation.user.name}.`);
      setIsBlocked(false);
      // Persist unblock flag locally for current and cached conversations
      setSelectedConversation(prev => prev ? { ...prev, blocked: false } : prev);
      setConversations(prev => prev.map(conv =>
        conv.id === selectedConversation.id ? { ...conv, blocked: false } : conv
      ));
      setCachedConversations(prev => prev ? prev.map(conv =>
        conv.id === selectedConversation.id ? { ...conv, blocked: false } : conv
      ) : []);
      setShowDropdown(false);
    } catch (err) {
      console.error('Error unblocking user:', err);
      setError(formatFetchError(err, 'Failed to unblock user. Please try again.'));
    } finally {
      setIsBlocking(false);
    }
  };

  const handleReportUser = () => {
    if (!selectedConversation) return;
    setShowDropdown(false);
    // Show banner instead of toast
    setShowReportInstructionBanner(true);
  };

  const handleReportSubmit = async (reportData) => {
    if (!selectedConversation) return;

    setIsReporting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Please login again to report this user.');
        return;
      }

      const { response, data } = await fetchJson(`${API_BASE_URL}/api/messages/report`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          userId: selectedConversation.id,
          reason: reportData.reason,
          description: reportData.description
        })
      });

      if (!response.ok || !data.success) {
        setError(data.message || 'Failed to report user');
        return;
      }

      success(`Thank you for reporting ${selectedConversation.user.name}. Our team will review this conversation.`);
      setShowReportModal(false);
    } catch (err) {
      console.error('Error reporting user:', err);
      setError(formatFetchError(err, 'Failed to report user. Please try again.'));
    } finally {
      setIsReporting(false);
    }
  };

  const handleDeleteSingleMessage = async (messageId) => {
    if (!messageId) return;

    // Close dropdown
    setMessageDropdownId(null);

    // Optimistic update
    setSelectedConversation(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        messages: prev.messages.filter(msg => msg.id !== messageId)
      };
    });

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      await fetchJson(`${API_BASE_URL}/api/messages/${messageId}/delete`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Also update lists
      updateConversationsState(prev => prev.map(conv => {
        if (conv.id === selectedConversation?.id) {
          return {
            ...conv,
            messages: conv.messages.filter(msg => msg.id !== messageId)
          };
        }
        return conv;
      }));

    } catch (err) {
      console.error('Error deleting message:', err);
      // Revert if needed, or just show error
      showError('Failed to delete message');
    }
  };

  const handleReportMessageOpen = (message) => {
    setMessageToReport(message);
    setShowMessageReportModal(true);
    setMessageDropdownId(null);
  };

  const handleReportMessageSubmit = async (reportData) => {
    if (!messageToReport) return;

    setIsReporting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const { response, data } = await fetchJson(`${API_BASE_URL}/api/messages/report-message`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          messageId: messageToReport.id,
          reason: reportData.reason,
          description: reportData.description
        })
      });

      if (!response.ok || !data.success) {
        showError(data?.message || 'Failed to report message');
        return;
      }

      success('Message reported successfully. We will review it shortly.');
      setShowMessageReportModal(false);
      setMessageToReport(null);
    } catch (err) {
      console.error('Error reporting message:', err);
      showError('Failed to report message');
    } finally {
      setIsReporting(false);
    }
  };

  const handleResendMessage = (message) => {
    // Basic resend logic - fill input and delete old failed message
    if (!message) return;
    setMessageInput(message.text || '');
    handleDeleteSingleMessage(message.id);
  };

  const handleRespondToRequest = async (senderId, action) => {
    if (!senderId) return;
    setIsRespondingRequest(true);
    try {
      const token = localStorage.getItem('token');
      const { response, data } = await fetchJson(`${API_BASE_URL}/api/messages/requests/respond`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ senderId, action })
      });

      if (response.ok && data.success) {
        if (action === 'accept') {
          success("✅ You've accepted the message request.");
          fetchConversations();
          fetchPendingRequests();
        } else {
          warning("Declined the message request.");
          setPendingRequests(prev => prev.filter(req => (req.from?._id || req.from)?.toString() !== senderId.toString()));
        }

        if (selectedConversation?.id === senderId) {
          setSelectedConversation(null);
          if (isMobile) setShowChatList(true);
        }
      } else {
        showError(data?.message || 'Failed to respond to request');
      }
    } catch (err) {
      console.error('Error responding to request:', err);
      showError('Something went wrong. Please try again.');
    } finally {
      setIsRespondingRequest(false);
    }
  };

  const orderedMessages = useMemo(() => {
    const list = Array.isArray(selectedConversation?.messages) ? selectedConversation.messages : [];
    return list
      .slice()
      .sort((a, b) => {
        const aTime = a?.sentAt ? new Date(a.sentAt).getTime() : 0;
        const bTime = b?.sentAt ? new Date(b.sentAt).getTime() : 0;
        if (aTime && bTime) return aTime - bTime;
        if (aTime && !bTime) return 1;
        if (!aTime && bTime) return -1;
        return 0;
      });
  }, [selectedConversation?.messages]);

  const combinedChatItems = useMemo(() => {
    const requestItems = (pendingRequests || []).map(req => {
      const currentUserIdStr = (user?._id || user?.id || '').toString();
      const fromIdStr = (req.from?._id || req.from || '').toString();
      const isSentByMe = fromIdStr === currentUserIdStr;
      const partner = isSentByMe ? req.to : req.from;

      if (!partner) return null;
      const partnerIdStr = (partner?._id || partner || '').toString();

      return {
        id: partnerIdStr,
        type: 'request',
        user: {
          name: `${partner.firstName || 'Someone'} ${partner.lastName || ''}`,
          photo: getProfilePhotoUrl(partner.profilePhoto),
          lastActive: isSentByMe ? 'Request Sent' : 'Wants to message you'
        },
        lastMessage: isSentByMe ? '↗️ Request Sent' : '📩 New Message Request',
        timestamp: formatMessageTime(req.sentAt || req.createdAt),
        unread: isSentByMe ? 0 : 1,
        originalRequest: req
      };
    }).filter(Boolean);

    const transformedConvs = conversations.map(c => ({
      ...c,
      id: c.id?.toString()
    }));

    return [...requestItems, ...transformedConvs];
  }, [pendingRequests, conversations, user]);

  const filteredChatItems = useMemo(() => {
    if (activeTab === 'requests') {
      return combinedChatItems.filter(item => item.type === 'request');
    }
    return combinedChatItems.filter(item => item.type !== 'request');
  }, [combinedChatItems, activeTab]);

  // Show loading state if user data is not available
  if (!user || !user.id) {
    return (
      <>
        {/* Show loading state if user data is not available */}
        <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="flex h-[600px] items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-romantic-500 mb-4"></div>
              <p className="text-gray-600">Loading your conversations...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Report Instruction Banner */}
      {showReportInstructionBanner && (
        <div className="max-w-6xl mx-auto mb-4 bg-blue-50 border-l-4 border-blue-500 rounded-lg shadow-md overflow-hidden">
          <div className="p-4 flex items-start justify-between">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-0.5">
                <FiInfo className="text-blue-600 text-2xl" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-blue-900 mb-1">How to Report a Message</h3>
                <p className="text-sm text-blue-800 leading-relaxed">
                  To report a specific message, click the <strong>info icon (i)</strong> located to the right of any received message.
                  This allows our moderation team to review the exact content you're reporting.
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowReportInstructionBanner(false)}
              className="flex-shrink-0 ml-4 text-blue-600 hover:text-blue-800 transition-colors"
              aria-label="Close banner"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="flex h-[600px]">
          {/* Conversation List - show on desktop, or on mobile when showChatList is true */}
          {(!isMobile || showChatList) && (
            <div className="w-full md:w-80 border-r border-gray-200 flex flex-col" style={{ minWidth: 0 }}>
              <div className="p-4 border-b border-gray-200">
                <h1 className="text-2xl font-romantic font-bold text-gray-900">Messages</h1>
                <div className="relative mt-4">
                  <input
                    type="text"
                    placeholder="Search conversations..."
                    className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-romantic-500 focus:border-transparent"
                  />
                  <FiSearch className="absolute left-3 top-2.5 text-gray-400" size={18} />
                </div>
                
                {/* Tab Toggle */}
                <div className="flex bg-gray-100 rounded-lg p-1 mt-4">
                  <button 
                    onClick={() => setActiveTab('chats')} 
                    className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-colors ${activeTab === 'chats' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    Chats
                  </button>
                  <button 
                    onClick={() => setActiveTab('requests')} 
                    className={`relative flex-1 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center justify-center gap-2 ${activeTab === 'requests' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    Message Requests
                    {pendingRequests.length > 0 && (
                      <span className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse shadow-sm"></span>
                    )}
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto scrollbar-hide">
                {initialLoading ? (
                  // Skeleton loading states
                  Array.from({ length: 5 }).map((_, index) => (
                    <div key={index} className="p-4 border-b border-gray-100 animate-pulse">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                        <div className="flex-1">
                          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : filteredChatItems.length === 0 ? (
                  <div className="p-8 text-center">
                    <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FiMessageSquare className="text-gray-300" size={24} />
                    </div>
                    <p className="text-gray-500 text-sm">{activeTab === 'requests' ? 'No pending requests' : 'No messages yet'}</p>
                    {activeTab === 'chats' && <p className="text-xs text-gray-400 mt-1">Start a conversation from profiles!</p>}
                  </div>
                ) : (
                  filteredChatItems.map((item) => {
                    const itemIdStr = item.id?.toString();
                    const isSelected = selectedConversation?.id?.toString() === itemIdStr;
                    const isHighlighted = hoveredConversationId === itemIdStr; // highlight only on hover
                    const isRequest = item.type === 'request';

                    const nameClass = theme === 'dark'
                      ? isHighlighted
                        ? 'text-slate-900'
                        : 'text-white'
                      : 'text-gray-900';

                    const mutedClass = theme === 'dark'
                      ? isHighlighted
                        ? 'text-slate-800'
                        : 'text-gray-300'
                      : 'text-gray-500';

                    const lastMessageClass = theme === 'dark'
                      ? isHighlighted
                        ? 'text-slate-800'
                        : 'text-gray-300'
                      : 'text-gray-600';

                    const selectedClass = isSelected
                      ? theme === 'dark'
                        ? 'border-l-4 border-l-romantic-500' // dark bg, colored border only
                        : 'bg-rose-50 border-l-4 border-l-romantic-500'
                      : '';

                    return (
                      <div
                        key={item.id}
                        onClick={() => handleSelectConversation(item)}
                        onMouseEnter={() => setHoveredConversationId(item.id)}
                        onMouseLeave={() => setHoveredConversationId(null)}
                        className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${selectedClass}`}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="relative">
                            <img
                              src={item.user.photo}
                              alt={item.user.name}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                            {item.type !== 'request' && onlineUsers.has(item.id.toString()) && (
                              <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-start">
                              <div className="flex items-center space-x-2">
                                <h3 className={`font-semibold truncate ${nameClass}`}>
                                  {item.user.name}
                                </h3>
                                {(item.unread > 0 || isRequest) && (
                                  <span className={`${isRequest ? 'bg-romantic-500 animate-pulse' : 'bg-red-500'} text-white text-[10px] leading-[16px] px-2 rounded-full min-w-[18px] h-[18px] flex items-center justify-center`}>
                                    {isRequest ? '!' : (item.unread > 9 ? '9+' : item.unread)}
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-col items-end">
                                <span className={`text-xs ${mutedClass}`}>
                                  {item.timestamp}
                                </span>
                                <div className="relative mt-1">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setMenuOpenItemId(menuOpenItemId === itemIdStr ? null : itemIdStr);
                                    }}
                                    className="p-1 hover:bg-white/20 rounded-full text-gray-400 hover:text-romantic-500 transition-colors"
                                  >
                                    <FiMoreVertical size={16} />
                                  </button>
                                  {menuOpenItemId === itemIdStr && (
                                    <div 
                                      className="absolute right-0 top-6 w-36 bg-white rounded-lg shadow-xl border border-gray-100 py-1 z-30"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (window.confirm(`Are you sure you want to delete this chat with ${item.user.name}? This cannot be undone.`)) {
                                            handleDeleteConversation(itemIdStr);
                                          }
                                        }}
                                        className="w-full px-3 py-2 text-left text-[11px] font-bold text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                                      >
                                        <FiTrash2 size={13} />
                                        Delete Chat
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            <p className={`text-sm truncate ${lastMessageClass} ${(item.unread > 0 || isRequest) ? 'font-semibold text-gray-900' : ''}`}>
                              {isRequest ? '📩 New Message Request' : renderEmojiTextSmall(item.lastMessage)}
                            </p>
                            <div className="flex justify-between items-center mt-1">
                              <span className={`text-xs ${mutedClass}`}>
                                {item.user.lastActive}
                              </span>
                              {isRequest && (
                                <span className="text-[10px] text-romantic-600 font-bold bg-romantic-50 px-1.5 py-0.5 rounded uppercase flex items-center gap-1">
                                  <span className="w-1 h-1 bg-romantic-500 rounded-full animate-ping"></span>
                                  Request
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Chat Area - Show on desktop or mobile when conversation is selected */}
          {(!isMobile || !showChatList) && (
            <div className="flex-1 flex flex-col" style={{ minHeight: '0' }}>
              {selectedConversation ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {/* Back button for mobile */}
                      {isMobile && (
                        <button
                          onClick={handleBackToChatList}
                          className="p-2 rounded-lg hover:bg-gray-100 transition-colors mr-2"
                        >
                          <FiArrowLeft className="text-gray-600" size={20} />
                        </button>
                      )}
                      <img
                        src={selectedConversation.user.photo}
                        alt={selectedConversation.user.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {selectedConversation.user.name}
                        </h3>
                        <p className={`text-sm ${onlineUsers.has(selectedConversation.id.toString()) ? 'text-green-600' : 'text-gray-500'}`}>
                          {onlineUsers.has(selectedConversation.id.toString()) ? 'Online' : 'Offline'}
                        </p>
                      </div>
                    </div>

                    {/* Three Dots Dropdown */}
                    <div className="relative" ref={dropdownRef}>
                      <button
                        onClick={() => setShowDropdown(!showDropdown)}
                        className="text-gray-500 hover:text-romantic-500 transition-colors p-2 rounded-lg hover:bg-gray-100"
                      >
                        <FiMoreVertical size={24} />
                      </button>

                      {showDropdown && (
                        <div className="absolute right-0 top-12 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-10">
                          <button
                            onClick={handleDeleteConversation}
                            disabled={isDeleting}
                            className={`w-full px-4 py-3 text-left transition-colors flex items-center space-x-3 ${isDeleting
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-gray-700 hover:bg-red-50 hover:text-red-600'
                              }`}
                          >
                            {isDeleting ? (
                              <>
                                <svg className="w-5 h-5 animate-spin text-red-500" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h3z"></path>
                                </svg>
                                <span>Deleting…</span>
                              </>
                            ) : (
                              <>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                <span>Delete Conversation</span>
                              </>
                            )}
                          </button>

                          <button
                            onClick={isBlocked ? handleUnblockUser : handleBlockUser}
                            disabled={isBlocking}
                            className={`w-full px-4 py-3 text-left transition-colors flex items-center space-x-3 ${isBlocking
                              ? 'text-gray-400 cursor-not-allowed'
                              : isBlocked
                                ? 'text-gray-700 hover:bg-orange-50 hover:text-orange-600'
                                : 'text-gray-700 hover:bg-orange-50 hover:text-orange-600'
                              }`}
                          >
                            {isBlocking ? (
                              <svg className="w-5 h-5 animate-spin text-orange-500" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h3z"></path>
                              </svg>
                            ) : (
                              <FiSlash size={20} />
                            )}
                            <span>{isBlocked ? 'Unblock User' : 'Block User'}</span>
                          </button>

                          <button
                            onClick={handleReportUser}
                            className="w-full px-4 py-3 text-left text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors flex items-center space-x-3"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <span>Report User</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Messages */}
                  <div ref={messagesContainerRef} className="flex-1 p-2 overflow-y-auto bg-gray-50 relative scrollbar-thin" style={{ minHeight: '0' }}>
                    {selectedConversation.type === 'request' ? (
                      <div className="flex flex-col items-center justify-center min-h-full text-center p-6 bg-white rounded-2xl m-4 shadow-sm border border-gray-100">
                        <div 
                          className="w-24 h-24 rounded-full flex items-center justify-center mb-6 ring-8 ring-rose-50 overflow-hidden shadow-md cursor-pointer hover:ring-rose-100 transition-all active:scale-95"
                          onClick={() => navigate(`/dashboard/user-profile/${selectedConversation.id}`)}
                        >
                          <img src={selectedConversation.user.photo} alt="" className="w-full h-full object-cover" />
                        </div>
                        <h4 className="text-2xl font-bold text-gray-900 mb-2">Message Request</h4>
                        <p className="text-gray-600 mb-2">
                          <strong 
                            className="text-blue-600 hover:text-blue-700 cursor-pointer hover:underline transition-colors"
                            onClick={() => navigate(`/dashboard/user-profile/${selectedConversation.id}`)}
                          >
                            {selectedConversation.user.name}
                          </strong> {" "} would like to start a conversation with you.
                        </p>
                        <p className="text-gray-400 text-sm max-w-sm leading-relaxed mb-10">
                          They won't know you've seen this request until you accept it. Once accepted, you can both send messages and photos.
                        </p>

                        <div className="flex items-center gap-6">
                          <button
                            onClick={() => handleRespondToRequest(selectedConversation.id, 'decline')}
                            disabled={isRespondingRequest}
                            className="px-10 py-3 rounded-full border-2 border-gray-300 text-gray-600 font-bold hover:bg-gray-100 transition-all disabled:opacity-50 active:scale-95"
                          >
                            {isRespondingRequest ? 'Processing...' : 'Decline'}
                          </button>
                          <button
                            onClick={() => handleRespondToRequest(selectedConversation.id, 'accept')}
                            disabled={isRespondingRequest}
                            className="px-12 py-3 rounded-full font-bold text-white shadow-xl transition-all hover:shadow-rose-200/50 hover:scale-105 active:scale-95 disabled:opacity-50 disabled:scale-100 flex items-center gap-2"
                            style={{ background: 'linear-gradient(135deg, #f43f5e 0%, #ec4899 100%)' }}
                          >
                            {isRespondingRequest ? 'Processing...' : 'Accept Request'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="min-h-full flex flex-col justify-end space-y-2">
                        {/* Safety Disclaimer */}
                        <div className="flex flex-col items-center justify-center py-6 px-4 mb-4 text-center space-y-3 bg-gray-50/50">
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-w-lg w-full shadow-sm">
                            <div className="flex items-center justify-center mb-2 text-yellow-800">
                              <FiShield className="text-xl mr-2" />
                              <p className="font-semibold text-sm">Your messages are private and secure.</p>
                            </div>

                            <div className="text-xs text-gray-600 space-y-2 mt-3 text-left bg-white p-3 rounded border border-gray-100">
                              <p className="font-bold flex items-center text-orange-600">
                                <FiAlertTriangle className="mr-1" /> Safety Tips:
                              </p>
                              <ul className="list-disc pl-5 space-y-1">
                                <li>We do not routinely read your private conversations.</li>
                                <li>Messages may be reviewed only if required by law or after a user report.</li>
                                <li>Report any suspicious behavior immediately.</li>
                                <li>Never share financial information.</li>
                                <li>Verify profiles before sharing personal details.</li>
                                <li>Click the info icon (i) next to any message to report it.</li>
                              </ul>

                              <div className="pt-2 mt-2 border-t border-gray-100 flex justify-center">
                                <button
                                  onClick={handleReportUser}
                                  className="bg-red-50 hover:bg-red-100 text-red-600 text-xs font-bold py-1.5 px-4 rounded-full transition-colors border border-red-200 flex items-center"
                                >
                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                  </svg>
                                  Report User
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                      {orderedMessages.map((message, index) => {
                        // Use consistent comparison to determine if message is from current user
                        const isMyMessage = message.sender === 'me' || message.senderId?.toString() === user?.id;

                        // Check if this is the first message from this sender in a sequence (for profile picture)
                        const prevMessage = index > 0 ? orderedMessages[index - 1] : null;
                        const prevWasMyMessage = prevMessage?.sender === 'me' || prevMessage?.senderId?.toString() === user?.id;
                        const currentWasMyMessage = isMyMessage;

                        // Show profile picture only for first message from other users in a sequence
                        const showProfilePicture = !currentWasMyMessage &&
                          (!prevMessage ||
                            prevWasMyMessage || // Previous message was from me
                            (prevMessage?.senderId?.toString() !== (message.senderId || message.from)?.toString()));

                        return (
                          <div key={message.id || index} className={`flex ${isMyMessage ? 'justify-end' : 'justify-start'}`}>
                            {!isMyMessage && showProfilePicture && (
                              <img
                                src={message.senderInfo?.photo || '/api/placeholder/40/40'}
                                alt={message.senderInfo?.name || 'User'}
                                className="w-8 h-8 rounded-full object-cover mr-2 flex-shrink-0"
                              />
                            )}
                            {!isMyMessage && !showProfilePicture && (
                              <div className="w-8 h-8 mr-2 flex-shrink-0"></div>
                            )}
                            <div className="flex items-end gap-2 relative group">
                              {/* Sent Message Options (Left Side) */}
                              {isMyMessage && (
                                <div className="relative opacity-100 transition-opacity flex flex-col items-center gap-1">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setMessageDropdownId(messageDropdownId === message.id ? null : message.id);
                                    }}
                                    className="p-1 rounded-full text-gray-400 hover:text-romantic-500 hover:bg-gray-100 transition-colors"
                                  >
                                    <FiInfo size={16} />
                                  </button>

                                  {/* Dropdown */}
                                  {messageDropdownId === message.id && (
                                    <div className="absolute top-8 right-0 w-40 bg-white rounded-lg shadow-xl border border-gray-200 z-50 py-1 overflow-hidden">
                                      {/* Resend Action - Only if failed */}
                                      {message.status === 'failed' && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleResendMessage(message);
                                          }}
                                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                                        >
                                          <FiRefreshCw size={14} />
                                          <span>Resend</span>
                                        </button>
                                      )}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteSingleMessage(message.id);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                                      >
                                        <FiTrash2 size={14} />
                                        <span>Delete Message</span>
                                      </button>
                                    </div>
                                  )}

                                  {/* Failed Indicator */}
                                  {message.status === 'failed' && (
                                    <span className="text-[10px] text-red-500 font-bold leading-none">Failed</span>
                                  )}
                                </div>
                              )}

                              <div className={`max-w-xs lg:max-w-md ${isMyMessage ? 'bg-rose-500 text-white' : 'bg-gray-200 text-gray-800'} rounded-2xl px-4 py-2 shadow-sm ${isMyMessage ? 'rounded-br-none' : 'rounded-bl-none'}`}>
                                {message.type === 'image' && message.imageUrl ? (
                                  <div className="space-y-2">
                                    <img
                                      src={message.imageUrl}
                                      alt="Sent"
                                      className="rounded-lg max-h-80 object-cover"
                                    />
                                    {message.text && (
                                      <p className="text-sm break-words">{renderEmojiText(message.text)}</p>
                                    )}
                                  </div>
                                ) : (
                                  <p className="text-sm break-words">{renderEmojiText(message.text)}</p>
                                )}
                                <div className={`flex items-center justify-end space-x-1 mt-1 ${isMyMessage ? 'text-rose-200' : 'text-gray-500'}`}>
                                  <span className="text-xs">{message.timestamp}</span>
                                  {isMyMessage && (
                                    <div className="ml-2 text-xs">
                                      {message.temp ? (
                                        <span className="text-rose-200">✓</span> // Single checkmark for sent
                                      ) : (message.isRead || message.status === 'read' || message.status === 'delivered') ? (
                                        <span className="text-rose-200">✓✓</span> // Double checkmarks only when delivered/read
                                      ) : (
                                        <span className="text-rose-200">✓</span> // Single checkmark when recipient offline/unconfirmed
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Received Message Options (Right side) */}
                              {!isMyMessage && (
                                <div className="relative opacity-100 transition-opacity">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setMessageDropdownId(messageDropdownId === message.id ? null : message.id);
                                    }}
                                    className="p-1 rounded-full text-gray-400 hover:text-romantic-500 hover:bg-gray-100 transition-colors"
                                  >
                                    <FiInfo size={16} />
                                  </button>

                                  {/* Dropdown */}
                                  {messageDropdownId === message.id && (
                                    <div className="absolute top-8 left-0 w-40 bg-white rounded-lg shadow-xl border border-gray-200 z-50 py-1 overflow-hidden">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleReportMessageOpen(message);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                                      >
                                        <FiFlag size={14} />
                                        <span>Report Message</span>
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {uploadingImage && (
                        <div className="flex justify-end">
                          <div className="bg-gray-300 rounded-2xl rounded-tr-none px-4 py-2 shadow-sm">
                            <div className="flex items-center space-x-2">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-romantic-500"></div>
                              <span className="text-gray-600 text-sm">Uploading image...</span>
                            </div>
                          </div>
                        </div>
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                  )}

                  {/* Scroll to Top Button */}
                    {showScrollTop && (
                      <button
                        onClick={scrollToTop}
                        className="absolute top-4 right-4 bg-romantic-500 text-white p-3 rounded-full shadow-lg hover:bg-romantic-600 transition-all duration-200 z-10"
                        title="Scroll to top"
                      >
                        <FiArrowUp size={16} />
                      </button>
                    )}

                    {/* Scroll to Bottom Button */}
                    {showScrollBottom && (
                      <button
                        onClick={() => scrollToBottom(true)}
                        className="absolute bottom-4 right-4 bg-romantic-500 text-white p-3 rounded-full shadow-lg hover:bg-romantic-600 transition-all duration-200 z-10"
                        title="Scroll to bottom (show input field)"
                      >
                        <FiArrowDown size={16} />
                      </button>
                    )}
                  </div>

                  {/* Typing Indicator */}
                  {typingUser && selectedConversation.type !== 'request' && (
                    <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        </div>
                        <span>{typingUser} is typing...</span>
                      </div>
                    </div>
                  )}



                  {/* Message Input */}
                  {selectedConversation.type !== 'request' && (
                    <div className="p-3 border-t border-gray-200 bg-white relative">
                    {showEmojiPicker && (
                      <div ref={emojiPickerRef} className="absolute bottom-20 left-4 z-50">
                        <div className="bg-white rounded-lg border border-gray-200 shadow-lg">
                          <Picker
                            onEmojiClick={handleEmojiSelect}
                            theme="light"
                            emojiStyle={EmojiStyle.FACEBOOK}
                            pickerStyle={{
                              width: '320px',
                              height: '400px'
                            }}
                          />
                        </div>
                      </div>
                    )}

                    <div className="flex items-center space-x-3">
                      {/* Image Upload Button */}
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="flex-shrink-0 text-gray-500 hover:text-romantic-500 transition-colors"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </button>

                      {/* Emoji Picker Button */}
                      <button
                        ref={emojiButtonRef}
                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                        className="flex-shrink-0 text-gray-500 hover:text-romantic-500 transition-colors"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>

                      {/* Message Input */}
                      <div className="flex-1 relative">
                        <input
                          type="text"
                          value={messageInput}
                          onChange={handleMessageChange}
                          onKeyPress={handleKeyPress}
                          placeholder="Type a message..."
                          className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-full focus:ring-2 focus:ring-romantic-500 focus:border-transparent text-gray-900 placeholder-gray-400"
                        />
                      </div>

                      {/* Send Button */}
                      <button
                        onClick={handleSendMessage}
                        disabled={!messageInput.trim()}
                        className="flex-shrink-0 bg-romantic-500 text-white p-3 rounded-full font-semibold hover:bg-romantic-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                      </button>
                    </div>

                    {/* Hidden file input */}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                      accept="image/*"
                      className="hidden"
                    />
                  </div>
                )}
                </>
              ) : (
                /* Empty State */
                <div className="flex-1 flex flex-col items-center justify-center text-gray-500 p-8">
                  <div className="text-6xl mb-4">💬</div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">No conversation selected</h3>
                  <p className="text-center mb-8">Choose a conversation from the list to start messaging</p>

                  {/* Safety Disclaimer for Empty State */}

                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {selectedConversation && (
        <ReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          userName={selectedConversation.user.name}
          onSubmit={handleReportSubmit}
          isSubmitting={isReporting}
          type="profile"
        />
      )}
      {/* Report Modal for Specific Message */}
      <ReportModal
        isOpen={showMessageReportModal}
        onClose={() => setShowMessageReportModal(false)}
        userName="Message Content"
        onSubmit={handleReportMessageSubmit}
        isSubmitting={isReporting}
        type="post" // Use 'post' type to get content-appropriate reasons
      />
    </>
  );
};

export default Messages